/* ============================================
   Artemis Theme — Collection Filters (API-backed)
   Custom element: <collection-filters-api>

   Server-side faceted filtering for large collections (e.g. 10k+ trophies).
   Talks to the custom filter service:
     GET {api-base}/filter/collections/{handle}.json?<facets>&price_min&price_max&sort&page

   - Renders the sidebar, grid, chips and pagination from the JSON response.
   - Server computes matches + per-value counts, so this scales to any size.
   - If the collection has no config (404) or the index isn't ready (503), it
     leaves the server-rendered native grid untouched (graceful fallback).

   Mirrors the markup/classes of <collection-filters> + snippets/product-card.liquid
   so the existing filter CSS applies unchanged.
   ============================================ */

(function () {
  'use strict';

  if (customElements.get('collection-filters-api')) return;

  var RANGE_DEBOUNCE_MS = 350;

  var ICON_CHEVRON =
    '<svg class="icon icon-chevron-down filter-group__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
  var ICON_CLOSE =
    '<svg class="icon icon-close filter-chip__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  var RESERVED = { price_min: 1, price_max: 1, sort: 1, page: 1, section_id: 1 };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* --- Facet value ordering ---
     Apparel sizes must read Small → Medium → Large → XL → 2XL…, but their labels
     sort the wrong way alphabetically ("6X-Large" / "Adult 2X-Large" lexically
     precede "Adult Small"). sizeRank() maps a size label to a numeric rank;
     anything it can't parse as a size returns null and falls back to natural
     alpha order, so colors ("Charcoal", "Teal"…) and free-text values are
     unaffected. */

  // Parse the "X" multiplier in front of large/small: "x"→1, "xx"→2, "2x"→2,
  // "3"→3. Returns null when there's no recognizable multiplier (so a color
  // ending in l/s like "Coral"/"Teal" doesn't masquerade as a size).
  function xMult(head) {
    if (head === '') return null;
    if (/^x+$/.test(head)) return head.length;
    var m = head.match(/^(\d+)x?$/);
    if (m) return parseInt(m[1], 10);
    return null;
  }

  function sizeRank(label) {
    var s = String(label == null ? '' : label).toLowerCase();

    var group = 2; // adult / unprefixed
    if (/\b(toddler|infant|newborn|baby)\b/.test(s)) group = 0;
    else if (/\b(youth|junior|kids?|boys|girls)\b/.test(s) || /^\s*y(xs|s|m|l|xl)\b/.test(s)) group = 1;

    // "Tall"/"Long" variants sort immediately after their base size.
    var suffix = /\b(tall|long)\b/.test(s) ? 1 : 0;

    var core = s
      .replace(/\b(adult|youth|junior|kids?|boys|girls|toddler|infant|newborn|baby|mens?|womens?|ladies|unisex)\b/g, '')
      .replace(/\b(tall|long|big)\b/g, '')
      .replace(/[\s.\-_/]+/g, '');

    var base = null;
    if (core === 's' || core === 'sm' || core === 'small') base = 30;
    else if (core === 'm' || core === 'md' || core === 'med' || core === 'medium') base = 40;
    else if (core === 'l' || core === 'lg' || core === 'large') base = 50;
    else if (core === 'onesize' || core === 'os' || core === 'osfa' || core === 'onesizefitsall') base = 9000;
    else {
      var n;
      if (/large$/.test(core)) { n = xMult(core.replace(/large$/, '')); if (n) base = 50 + n * 10; }
      else if (/small$/.test(core)) { n = xMult(core.replace(/small$/, '')); if (n) base = 30 - n * 10; }
      else if (/l$/.test(core)) { n = xMult(core.replace(/l$/, '')); if (n) base = 50 + n * 10; }
      else if (/s$/.test(core)) { n = xMult(core.replace(/s$/, '')); if (n) base = 30 - n * 10; }
    }

    if (base == null) return null;
    return group * 100000 + base * 10 + suffix;
  }

  // Comparator for facet values: numeric size rank first (sizes ahead of
  // non-sizes), then natural alpha for everything else. Standalone so it can be
  // passed straight to Array#sort without a `this` binding.
  function compareValues(a, b) {
    var la = a && a.label != null ? String(a.label) : '';
    var lb = b && b.label != null ? String(b.label) : '';
    var ra = sizeRank(la);
    var rb = sizeRank(lb);
    if (ra != null && rb != null && ra !== rb) return ra - rb;
    if (ra != null && rb == null) return -1;
    if (ra == null && rb != null) return 1;
    return la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
  }

  class CollectionFiltersApi extends HTMLElement {
    connectedCallback() {
      this.apiBase = (this.getAttribute('data-api-base') || '').replace(/\/+$/, '');
      this.handle = this.getAttribute('data-collection-handle') || '';
      this.sidebarPos = this.getAttribute('data-sidebar-position') || 'left';
      this.currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      this.i18n = this._parseI18n();

      this.grid = this.querySelector('[data-filter-grid]');
      this.form = this.querySelector('[data-filter-form]');
      this.pager = this.querySelector('[data-server-pagination]');
      if (!this.apiBase || !this.handle || !this.grid) return;

      var countEl = this.querySelector('[data-filter-count]');
      this._countNoun =
        (countEl ? (countEl.textContent || '').replace(/^[\s\d.,]+/, '').trim() : '') || 'products';

      this.facetLabels = {}; // id -> { value: label }
      this.groupLabels = {}; // id -> label
      this.state = this._parseUrl();

      // The server renders the full, unfiltered collection into the grid. When
      // the URL already carries filters, that grid is wrong — hide it until the
      // first API render lands so it doesn't flash then snap. On a plain
      // collection view the server grid already matches page 1, so leave it.
      if (this._hasActiveQuery()) this.classList.add('is-initial-load');

      this._rangeTimer = null;
      this._bindShell();
      this._bindControls();

      this._onPopState = this._onPopState.bind(this);
      window.addEventListener('popstate', this._onPopState);

      this._load(false);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this._onPopState);
    }

    /* --- i18n --- */
    _parseI18n() {
      var el = this.querySelector('[data-filter-i18n]');
      if (!el) return {};
      try { return JSON.parse(el.textContent || '{}'); } catch (e) { return {}; }
    }
    _t(key, fallback) { return (this.i18n && this.i18n[key]) || fallback; }

    /* --- money --- */
    _money(cents) {
      var amount = (cents || 0) / 100;
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(amount);
      } catch (e) {
        return '$' + amount.toFixed(2);
      }
    }

    /* --- State <-> URL --- */
    _parseUrl() {
      var params = new URLSearchParams(window.location.search);
      var filters = {};
      params.forEach(function (value, key) {
        if (RESERVED[key] || !value) return;
        var set = new Set();
        value.split(',').forEach(function (v) {
          var t = v.trim().toLowerCase();
          if (t) set.add(t);
        });
        if (set.size) filters[key] = set;
      });
      var price = { min: null, max: null };
      if (params.get('price_min')) price.min = parseFloat(params.get('price_min'));
      if (params.get('price_max')) price.max = parseFloat(params.get('price_max'));
      var page = parseInt(params.get('page'), 10);
      return {
        filters: filters,
        price: price,
        sort: params.get('sort') || 'manual',
        page: page > 0 ? page : 1
      };
    }

    // True when the URL carries any filter, price bound, non-default sort, or a
    // page past the first — i.e. the server-rendered grid won't match the API.
    _hasActiveQuery() {
      var f = this.state.filters;
      for (var id in f) {
        if (f.hasOwnProperty(id) && f[id] && f[id].size) return true;
      }
      return this.state.price.min != null || this.state.price.max != null ||
        (this.state.sort && this.state.sort !== 'manual') || this.state.page > 1;
    }

    _endpoint() {
      var p = new URLSearchParams();
      var f = this.state.filters;
      for (var id in f) {
        if (f.hasOwnProperty(id) && f[id] && f[id].size) p.set(id, Array.from(f[id]).join(','));
      }
      if (this.state.price.min != null) p.set('price_min', this.state.price.min);
      if (this.state.price.max != null) p.set('price_max', this.state.price.max);
      if (this.state.sort && this.state.sort !== 'manual') p.set('sort', this.state.sort);
      if (this.state.page > 1) p.set('page', this.state.page);
      var qs = p.toString();
      return this.apiBase + '/filter/collections/' + encodeURIComponent(this.handle) + '.json' + (qs ? '?' + qs : '');
    }

    _syncUrl() {
      var qs = this._endpoint().split('?')[1] || '';
      history.replaceState({ filters: true }, '', window.location.pathname + (qs ? '?' + qs : ''));
    }

    _onPopState() {
      this.state = this._parseUrl();
      this._load(false);
    }

    /* --- Fetch --- */
    _load(scroll) {
      var self = this;
      this.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');
      fetch(this._endpoint(), { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (res.status === 404 || res.status === 503) { self._unavailable = true; return null; }
          if (!res.ok) throw new Error('filter api: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (!data) return;
          self._render(data);
          self._syncUrl();
          if (scroll) self._scrollToTop();
        })
        .catch(function () { /* leave the native server-rendered grid in place */ })
        .then(function () {
          self.removeAttribute('aria-busy');
          self.classList.remove('is-loading');
          // Fallback: if the API was unavailable or errored, _render never ran,
          // so unhide the native grid rather than leaving it blank forever.
          self.classList.remove('is-initial-load');
        });
    }

    _scrollToTop() {
      var top = this.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: top > 0 ? top : 0, behavior: 'smooth' });
    }

    /* --- Render --- */
    _render(data) {
      // Cache labels for chips.
      var self = this;
      (data.facets || []).forEach(function (f) {
        self.groupLabels[f.id] = f.label;
        if (f.values) {
          self.facetLabels[f.id] = self.facetLabels[f.id] || {};
          f.values.forEach(function (v) { self.facetLabels[f.id][v.value] = v.label; });
        }
      });

      this._renderSidebar(data.facets || []);
      this._renderGrid(data.products || []);
      this._renderCount(data.total || 0);
      this._renderChips();
      this._renderPagination(data.page || 1, data.pages || 1);
      this._applyStateToInputs();
      this._revealSidebar();

      var empty = this.querySelector('[data-filter-empty]');
      if (empty) empty.hidden = (data.total || 0) !== 0;

      // First real grid is on screen — reveal it (no-op after the first render).
      this.classList.remove('is-initial-load');
    }

    _renderCount(total) {
      var el = this.querySelector('[data-filter-count]');
      if (el) el.textContent = total + ' ' + this._countNoun;
    }

    _renderGrid(products) {
      var self = this;
      this.grid.innerHTML = products.map(function (p) { return self._card(p); }).join('');
      this.grid.classList.add('is-visible');
    }

    _card(p) {
      var url = p.url || ('/products/' + p.handle);
      var media = p.image_url
        ? '<img src="' + esc(p.image_url) + '" alt="' + esc(p.title) + '" loading="lazy">'
        : '<div style="background-color: var(--color-neutral); position:absolute; inset:0;"></div>';
      var badges = p.available === false
        ? '<div class="product-card__badges"><span class="badge" style="background:var(--color-dark);color:#fff;">' +
          esc(this._t('sold_out', 'Sold out')) + '</span></div>'
        : '';
      var price = p.price != null
        ? '<div class="price"><span class="price__regular">' + esc(this._money(p.price)) + '</span></div>'
        : '';
      return (
        '<div class="product-card card-hover" data-product-card data-handle="' + esc(p.handle) + '">' +
          '<div class="product-card__media aspect-ratio aspect-ratio--square image-zoom">' +
            '<a href="' + esc(url) + '" tabindex="-1" aria-hidden="true">' + media + '</a>' + badges +
          '</div>' +
          '<div class="product-card__info">' +
            '<h3 class="product-card__title"><a href="' + esc(url) + '">' + esc(p.title) + '</a></h3>' +
            price +
          '</div>' +
        '</div>'
      );
    }

    /* --- Sidebar --- */
    _renderSidebar(facets) {
      if (!this.form) return;
      var self = this;
      this.form.innerHTML = facets.map(function (f) {
        return f.display === 'range' ? self._priceGroup(f) : self._listGroup(f);
      }).join('');
      if (window.ArtemisSwatch) window.ArtemisSwatch.paint(this.form);
    }

    _listGroup(f) {
      var self = this;
      var values = (f.values || []).slice().sort(compareValues);
      if (!values.length) return '';
      var truncate = f.truncate || 8;
      var isSwatch = f.display === 'swatch';
      var searchLabel = this._t('search', 'Search') + ' ' + esc(f.label);

      var searchHtml = values.length > truncate
        ? '<label class="filter-group__search"><span class="visually-hidden">' + searchLabel +
          '</span><input type="search" class="input filter-group__search-input" data-filter-search ' +
          'placeholder="' + searchLabel + '" autocomplete="off"></label>'
        : '';

      var items = values.map(function (v, idx) {
        var hidden = idx >= truncate ? ' is-hidden' : '';
        var trunc = idx >= truncate ? ' data-truncated' : '';
        var inner;
        if (isSwatch) {
          // Prefer a hex the index supplies (v.hex/v.swatch) — that's the only
          // source with 100% catalog coverage. Else the shared name→hex map,
          // else the raw value (legacy CSS-name behavior). paint() upgrades any
          // dot left on the name fallback once ArtemisSwatch is ready.
          var indexHex = v.hex || v.swatch;
          var swatch = indexHex || v.value;
          // Lock dots the index resolved so paint() won't override them with the
          // coarser name→hex fallback.
          var lock = indexHex ? ' data-swatch-locked' : '';
          inner =
            '<label class="filter-swatch">' +
              '<input type="checkbox" class="visually-hidden" data-facet="' + esc(f.id) + '" value="' + esc(v.value) + '">' +
              '<span class="filter-swatch__dot" data-swatch-name="' + esc(v.label) + '"' + lock + ' style="background:' + esc(swatch) + ';" aria-hidden="true"></span>' +
              '<span class="filter-swatch__name">' + esc(v.label) + '</span>' +
            '</label>';
        } else {
          inner =
            '<label class="filter-checkbox">' +
              '<input type="checkbox" data-facet="' + esc(f.id) + '" value="' + esc(v.value) + '">' +
              '<span class="filter-checkbox__label">' + esc(v.label) + '</span>' +
              '<span class="filter-checkbox__count" data-filter-value-count>(' + v.count + ')</span>' +
            '</label>';
        }
        return '<li class="filter-group__item' + hidden + '" data-filter-item data-filter-value="' +
          esc(v.value) + '"' + trunc + '>' + inner + '</li>';
      }).join('');

      var toggleHtml = values.length > truncate
        ? '<button type="button" class="filter-group__toggle link" data-filter-toggle aria-expanded="false">' +
          '<span data-filter-toggle-more>' + esc(this._t('show_more', 'Show more')) + '</span>' +
          '<span data-filter-toggle-less hidden>' + esc(this._t('show_less', 'Show less')) + '</span></button>'
        : '';

      return (
        '<details class="filter-group" data-filter-group data-filter-key="' + esc(f.id) + '" open>' +
          '<summary class="filter-group__summary">' +
            '<span class="filter-group__label">' + esc(f.label) + '</span>' +
            '<span class="filter-group__active-count" data-filter-active-group hidden></span>' +
            ICON_CHEVRON +
          '</summary>' +
          '<div class="filter-group__body">' +
            searchHtml +
            '<ul class="filter-group__list' + (isSwatch ? ' filter-group__list--swatches' : '') +
              '" role="list" data-filter-list>' + items + '</ul>' +
            toggleHtml +
          '</div>' +
        '</details>'
      );
    }

    _priceGroup(f) {
      var maxDollars = Math.ceil((f.max || 0) / 100);
      return (
        '<details class="filter-group" data-filter-group data-filter-key="price" open>' +
          '<summary class="filter-group__summary">' +
            '<span class="filter-group__label">' + esc(f.label || 'Price') + '</span>' +
            '<span class="filter-group__active-count" data-filter-active-group hidden></span>' +
            ICON_CHEVRON +
          '</summary>' +
          '<div class="filter-group__body">' +
            '<div class="filter-range" data-filter-range>' +
              '<div class="filter-range__inputs">' +
                '<label class="filter-range__field"><span class="filter-range__field-label">' +
                  esc(this._t('price_from', 'From')) + '</span>' +
                  '<input type="number" class="input filter-range__input" data-filter-range-min ' +
                  'min="0" max="' + maxDollars + '" step="1" inputmode="decimal" placeholder="0"></label>' +
                '<span class="filter-range__separator" aria-hidden="true">—</span>' +
                '<label class="filter-range__field"><span class="filter-range__field-label">' +
                  esc(this._t('price_to', 'To')) + '</span>' +
                  '<input type="number" class="input filter-range__input" data-filter-range-max ' +
                  'min="0" max="' + maxDollars + '" step="1" inputmode="decimal" placeholder="' + maxDollars + '"></label>' +
              '</div>' +
              '<p class="filter-range__hint">' + esc(this._money(f.min || 0)) + ' – ' + esc(this._money(f.max || 0)) + '</p>' +
            '</div>' +
          '</div>' +
        '</details>'
      );
    }

    _applyStateToInputs() {
      var self = this;
      this.querySelectorAll('input[data-facet]').forEach(function (input) {
        var set = self.state.filters[input.getAttribute('data-facet')];
        input.checked = !!(set && set.has(String(input.value).toLowerCase()));
      });
      var minEl = this.querySelector('[data-filter-range-min]');
      var maxEl = this.querySelector('[data-filter-range-max]');
      if (minEl) minEl.value = this.state.price.min != null ? this.state.price.min : '';
      if (maxEl) maxEl.value = this.state.price.max != null ? this.state.price.max : '';

      var sort = this.querySelector('[data-filter-sort]');
      if (sort) {
        for (var i = 0; i < sort.options.length; i++) {
          if (sort.options[i].getAttribute('data-sort-value') === this.state.sort) { sort.selectedIndex = i; break; }
        }
      }
      // Active-count badges per group.
      this.querySelectorAll('[data-filter-group]').forEach(function (group) {
        var id = group.getAttribute('data-filter-key');
        var n = id === 'price'
          ? (self.state.price.min != null || self.state.price.max != null ? 1 : 0)
          : (self.state.filters[id] ? self.state.filters[id].size : 0);
        var badge = group.querySelector('[data-filter-active-group]');
        if (badge) { if (n > 0) { badge.textContent = n; badge.hidden = false; } else { badge.hidden = true; } }
      });

      // Active-count badge on the mobile FILTERS button (total across all groups).
      var total = 0;
      for (var fid in this.state.filters) {
        if (this.state.filters.hasOwnProperty(fid) && this.state.filters[fid] && this.state.filters[fid].size) {
          total += this.state.filters[fid].size;
        }
      }
      if (this.state.price.min != null || this.state.price.max != null) total++;
      var totalBadge = this.querySelector('[data-filter-active-count]');
      if (totalBadge) {
        if (total > 0) { totalBadge.textContent = total; totalBadge.hidden = false; }
        else { totalBadge.hidden = true; }
      }
    }

    /* --- Chips --- */
    _renderChips() {
      var container = this.querySelector('[data-filter-chips]');
      if (!container) return;
      var self = this;
      var chips = [];

      for (var id in this.state.filters) {
        if (!this.state.filters.hasOwnProperty(id)) continue;
        var set = this.state.filters[id];
        if (!set || !set.size) continue;
        var groupLabel = this.groupLabels[id] || id;
        Array.from(set).forEach(function (val) {
          var display = (self.facetLabels[id] && self.facetLabels[id][val]) || val;
          chips.push(
            '<button type="button" class="filter-chip" data-filter-chip data-facet="' + esc(id) +
            '" data-value="' + esc(val) + '" aria-label="Remove ' + esc(display) + '">' +
              '<span class="filter-chip__text"><span class="filter-chip__group">' + esc(groupLabel) +
              ':</span> <span class="filter-chip__value">' + esc(display) + '</span></span>' + ICON_CLOSE +
            '</button>'
          );
        });
      }

      var pr = this.state.price;
      if (pr.min != null || pr.max != null) {
        var label = (pr.min != null ? this._money(pr.min * 100) : this._money(0)) + ' – ' +
          (pr.max != null ? this._money(pr.max * 100) : '∞');
        chips.push(
          '<button type="button" class="filter-chip" data-filter-chip data-facet="price" aria-label="Remove price filter">' +
            '<span class="filter-chip__text"><span class="filter-chip__group">' +
            esc(this.groupLabels.price || 'Price') + ':</span> <span class="filter-chip__value">' +
            esc(label) + '</span></span>' + ICON_CLOSE +
          '</button>'
        );
      }

      if (!chips.length) { container.hidden = true; container.innerHTML = ''; return; }
      container.hidden = false;
      container.innerHTML =
        '<span class="collection-chips__label">' + esc(this._t('applied_filters', 'Applied filters')) + ':</span>' +
        chips.join('') +
        '<button type="button" class="collection-chips__clear link" data-filter-clear>' +
        esc(this._t('clear_filters', 'Clear all')) + '</button>';
    }

    /* --- Pagination (server-side) --- */
    _renderPagination(page, pages) {
      if (!this.pager) return;
      this.pager.hidden = false;
      if (pages <= 1) { this.pager.innerHTML = ''; return; }

      var parts = [];
      var prevDisabled = page <= 1 ? ' is-disabled" aria-disabled="true' : '';
      parts.push('<button type="button" class="pagination__item pagination__item--prev' + prevDisabled +
        '" data-filter-page="' + (page - 1) + '"' + (page <= 1 ? ' disabled' : '') + '>‹</button>');

      var win = 2;
      var start = Math.max(1, page - win);
      var end = Math.min(pages, page + win);
      if (start > 1) parts.push(this._pageBtn(1, page));
      if (start > 2) parts.push('<span class="pagination__ellipsis">…</span>');
      for (var i = start; i <= end; i++) parts.push(this._pageBtn(i, page));
      if (end < pages - 1) parts.push('<span class="pagination__ellipsis">…</span>');
      if (end < pages) parts.push(this._pageBtn(pages, page));

      var nextDisabled = page >= pages ? ' is-disabled" aria-disabled="true' : '';
      parts.push('<button type="button" class="pagination__item pagination__item--next' + nextDisabled +
        '" data-filter-page="' + (page + 1) + '"' + (page >= pages ? ' disabled' : '') + '>›</button>');

      this.pager.innerHTML = '<nav class="pagination" role="navigation">' + parts.join('') + '</nav>';
    }

    _pageBtn(n, current) {
      var cls = 'pagination__item' + (n === current ? ' pagination__item--current" aria-current="page' : '');
      return '<button type="button" class="' + cls + '" data-filter-page="' + n + '">' + n + '</button>';
    }

    /* --- Events --- */
    _bindControls() {
      var self = this;

      this.addEventListener('change', function (e) {
        var t = e.target;
        if (t.matches('input[data-facet]')) {
          var id = t.getAttribute('data-facet');
          var val = String(t.value).toLowerCase();
          if (!self.state.filters[id]) self.state.filters[id] = new Set();
          if (t.checked) self.state.filters[id].add(val);
          else { self.state.filters[id].delete(val); if (!self.state.filters[id].size) delete self.state.filters[id]; }
          self.state.page = 1;
          self._load(false);
        } else if (t.matches('[data-filter-sort]')) {
          var opt = t.options[t.selectedIndex];
          self.state.sort = (opt && opt.getAttribute('data-sort-value')) || 'manual';
          self.state.page = 1;
          self._load(false);
        }
      });

      this.addEventListener('input', function (e) {
        if (e.target.matches('[data-filter-search]')) { self._filterValuesInGroup(e.target); return; }
        if (e.target.matches('[data-filter-range-min], [data-filter-range-max]')) self._onRangeInput();
      });

      this.addEventListener('click', function (e) {
        var page = e.target.closest('[data-filter-page]');
        if (page && !page.disabled) {
          self.state.page = parseInt(page.getAttribute('data-filter-page'), 10) || 1;
          self._load(true);
          return;
        }
        var chip = e.target.closest('[data-filter-chip]');
        if (chip) { self._removeChip(chip); return; }
        var clear = e.target.closest('[data-filter-clear]');
        if (clear) { self._clearAll(); return; }
        var toggle = e.target.closest('[data-filter-toggle]');
        if (toggle) { self._toggleTruncation(toggle); return; }
      });

      // Sort: stop the native onchange navigation.
      var sort = this.querySelector('[data-filter-sort]');
      if (sort) sort.removeAttribute('onchange');
    }

    _onRangeInput() {
      var self = this;
      var minEl = this.querySelector('[data-filter-range-min]');
      var maxEl = this.querySelector('[data-filter-range-max]');
      clearTimeout(this._rangeTimer);
      this._rangeTimer = setTimeout(function () {
        self.state.price.min = minEl && minEl.value !== '' ? parseFloat(minEl.value) : null;
        self.state.price.max = maxEl && maxEl.value !== '' ? parseFloat(maxEl.value) : null;
        self.state.page = 1;
        self._load(false);
      }, RANGE_DEBOUNCE_MS);
    }

    _removeChip(chip) {
      var facet = chip.getAttribute('data-facet');
      if (facet === 'price') {
        this.state.price = { min: null, max: null };
      } else {
        var value = chip.getAttribute('data-value');
        if (this.state.filters[facet]) {
          this.state.filters[facet].delete(value);
          if (!this.state.filters[facet].size) delete this.state.filters[facet];
        }
      }
      this.state.page = 1;
      this._load(false);
    }

    _clearAll() {
      this.state.filters = {};
      this.state.price = { min: null, max: null };
      this.state.page = 1;
      this._load(false);
    }

    _toggleTruncation(toggle) {
      var group = toggle.closest('[data-filter-group]');
      if (!group) return;
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      group.querySelectorAll('[data-truncated]').forEach(function (item) {
        item.classList.toggle('is-hidden', expanded);
      });
      toggle.setAttribute('aria-expanded', String(!expanded));
      var more = toggle.querySelector('[data-filter-toggle-more]');
      var less = toggle.querySelector('[data-filter-toggle-less]');
      if (more) more.hidden = !expanded;
      if (less) less.hidden = expanded;
    }

    _filterValuesInGroup(input) {
      var group = input.closest('[data-filter-group]');
      if (!group) return;
      var query = input.value.trim().toLowerCase();
      group.querySelectorAll('[data-filter-item]').forEach(function (item) {
        var label = item.getAttribute('data-filter-value') || '';
        item.classList.toggle('is-filtered-out', !(query === '' || label.indexOf(query) !== -1));
      });
    }

    _revealSidebar() {
      if (this._revealed) return;
      if (this.sidebarPos === 'none') return;
      // No sidebar shell was server-rendered (filtering disabled / aside absent).
      // Switching to the two-column sidebar grid here would auto-place the lone
      // .collection-main into the 280px sidebar track and collapse the grid, so
      // keep the full-width layout instead.
      if (!this.form) return;
      this.classList.remove('collection-layout--no-sidebar');
      this.classList.add('collection-layout--' + this.sidebarPos);
      this._revealed = true;
    }

    /* --- Mobile drawer --- */
    _bindShell() {
      var self = this;
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-filter-drawer-open]')) { e.preventDefault(); self._drawer(true); }
        else if (e.target.closest('[data-filter-drawer-close]')) { e.preventDefault(); self._drawer(false); }
      });
    }

    _drawer(open) {
      var sidebar = this.querySelector('[data-filter-sidebar]');
      var backdrop = this.querySelector('.collection-sidebar__backdrop');
      var trigger = this.querySelector('[data-filter-drawer-open]');
      if (!sidebar) return;
      sidebar.classList.toggle('is-open', open);
      if (backdrop) backdrop.classList.toggle('is-open', open);
      document.body.classList.toggle('filter-drawer-open', open);
      if (trigger) trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        if (!this._esc) { this._esc = function (e) { if (e.key === 'Escape') self._drawer(false); }; document.addEventListener('keydown', this._esc); }
      } else if (this._esc) {
        document.removeEventListener('keydown', this._esc); this._esc = null;
        if (trigger) trigger.focus();
      }
    }
  }

  customElements.define('collection-filters-api', CollectionFiltersApi);
})();
